/**
 * Created by simon zhao on 3/22/2022.
 * Description:
 * A utility for grid layout's persistent.
 * ------ maintenance history ------
 */
import { select, Store } from '@ngrx/store';
import { ColDef, ColumnApi, ExcelRow, GridApi } from 'ag-grid-community';
import { Observable, Subscription } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { businessConstants } from '../../../constants/business.constants';
import { AppState } from '../../../redux';
import { screensSelector } from '../../../redux/reducers/system.reducer';
import { SavedScreen } from '../../../tamalelibs/models/saved-screen.model';
import { DefaultColumnIDs, Field, FieldDefinition, FieldType } from '../../../tamalelibs/models/workflow.model';
import { SystemActionTypes } from '../../../tamalelibs/redux/actions/system.actions';
import { DateHelperWebService } from '../../../tamalelibs/services/date-helper.web.service';
import { ScreenService } from '../../../tamalelibs/services/screen.service';
import { UtilsService } from '../../../tamalelibs/services/utils.service';
import { BLANK_VALUE, ITEM_DELIMITER, WorkflowService } from '../../../tamalelibs/services/workflow.service';
import { GridLayoutModel, GROUP_DELIMINITER } from './grid-layout.model';
import { ControlType } from '../../../tamalelibs/models/template-control.model';

export class GridLayoutUtility {
    constructor(public screenId: string,
        public store: Store<AppState>, public screenService: ScreenService) {

    }

    private _destroySubscriptions: Array<Subscription> = [];

    static getGridHeader(gridColumnApi: ColumnApi) {
        const infoColumns = gridColumnApi.getColumns();
        const allColumns = gridColumnApi.getColumnState();
        const tempColumns = [];
        const pinnedLeftColumns = [];
        const pinnedRightColumns = [];
        const noPinnedColumns = [];
        let columns = [];
        allColumns.forEach((column, i) => {
            if (!column.hide) {
                if (column.pinned === 'left') {
                    pinnedLeftColumns.push(column);
                } else if (column.pinned === 'right') {
                    pinnedRightColumns.push(column);
                } else {
                    noPinnedColumns.push(column);
                }
            }
        });
        columns = [].concat(pinnedLeftColumns, noPinnedColumns, pinnedRightColumns);
        columns.forEach((column) => {
            const index = infoColumns.findIndex(item => item['colId'] === column.colId);
            if (index !== -1) {
                tempColumns.push({
                    name: infoColumns[index]['colDef'].headerName,
                    width: column.width,
                    id: column.colId
                });
            }
        });
        return tempColumns;
    }

    /**
    * obtains the cell content for exported excel file.
    * @param fieldDefinition the field definition.
    * @param params the context with a value property.
    * @returns the content rendered in the cell of exported XLS.
    */
    static parseCellValue(fieldDefinition: any, params: { value: any }, utils: UtilsService): any {
        const value: any = params.value;
        if (value === '' || value === BLANK_VALUE) {
            return BLANK_VALUE;
        }
        const factor: boolean = value || value === 0;
        if (fieldDefinition.columnType === FieldType.NUMBER && factor) {
            let decimal = 9;
            let zeroize = false;
            let separator = true;
            if (fieldDefinition.configuration) {
                if (fieldDefinition.configuration.decimals !== null && fieldDefinition.configuration.decimals !== undefined) {
                    decimal = fieldDefinition.configuration.decimals;
                    zeroize = true;
                }
                if (fieldDefinition.configuration.separator !== null && fieldDefinition.configuration.separator !== undefined) {
                    separator = fieldDefinition.configuration.separator;
                }
            }
            return utils.formatNumberStr(value.toString(), decimal, zeroize, separator);
        } else if (fieldDefinition.columnType === FieldType.CURRENCY && factor) {
            const isMinus: boolean = value < 0;
            let str: string = Math.abs(value).toString();
            str = utils.wipeOffScientificNotation(str);
            return isMinus ? '$(' + str + ')' : '$' + str;
        } else if ((fieldDefinition.columnType === FieldType.ENTITY || fieldDefinition.columnType === FieldType.User) && factor) {
            // support show an entity link for entity control(ed/met)
            const entityLinks = [];
            if (value && value instanceof Array) {
                // met
                let tooltipStr = '';
                value.forEach(item => {
                    entityLinks.push(`<a href="${WorkflowService.getEntityRelativeLink(item)}" target='_blank'>${item.shortName}</a>`);
                    if (tooltipStr.length > 0) {
                        tooltipStr += ITEM_DELIMITER;
                    }
                    tooltipStr += item.shortName;
                });

                return tooltipStr;
            } else {
                return !value ? BLANK_VALUE : !value.shortName ? value : value.shortName;
            }
        } else if (fieldDefinition.columnType === FieldType.DATE && factor) {
            return DateHelperWebService.getDateString(DateHelperWebService.parseDateFunc(+value), 'MM/dd/yyyy HH:mm');
        } else if (fieldDefinition.columnType === FieldType.FILE && factor) {
            return value.map(item => item.fileName).join(', ');
        } else if (fieldDefinition.columnType === FieldType.CHECK_LIST && factor) {
            // support show an entity link for entity control(ed/met)
            let displayStr = BLANK_VALUE;
            const checkLists = [];
            if (params && params['node'] && params['node']['data']) {
                params['node']['data'].task.forEach(task => {
                    task.taskDefinition?.template?.contains.forEach(control => {
                        // get all check list to handle the display in the view
                        if (control[0].type === ControlType.CHECK_LIST) {
                            checkLists.push(control[0]);
                        }
                    });
                });
            }
            const field = fieldDefinition.columnConfig;
            field.value = value;
            if (value) {
                const brief = WorkflowService.getFieldBriefContent(field, checkLists);
                if (brief) {
                    displayStr = brief;
                }
            }

            return displayStr;
        }
        return value;
    }

    /**
     * Export the given grid as an XLS file.
     * @param gridApi The grid API.
     * @param fileName String to use as the file name. If missing, the file name 'export.csv' will be used.
     * @param customHeader Content to put at the top of the file export. A 2D array of ExcelCell objects, see Custom Headers and Footers below. Alternatively, if you're exporting to CSV only, you can pass a multi-line string that is simply appended to the top of the file content.
     * @param sheetName The only sheet name of the exported excel file.
     * @param skipHeader Set to true if you don't want the first line to be the column header names. Default: false
     */
    static exportToXLS(gridApi: GridApi, gridColumnApi: ColumnApi, columnDefs: Array<any>, workflowService: WorkflowService, utils: UtilsService, fileName: string, sheetName = 'Sheet1', skipHeader = false) {
        const columns = gridColumnApi.getColumnState();
        const groupColumn = columns.filter(item => item.rowGroupIndex !== null);
        const groupColumnSettings = [];
        groupColumn.forEach(column => {
            const index = columnDefs.findIndex(item => item.colId === column.colId);
            if (index !== -1) {
                groupColumnSettings.push({ ...columnDefs[index], rowGroupIndex: column.rowGroupIndex });
            }
        });
        groupColumnSettings.sort((a, b) => a.rowGroupIndex - b.rowGroupIndex);
        const allCustomColumns = GridLayoutUtility.getGridHeader(gridColumnApi);
        const customHeader: ExcelRow[] = [];
        allCustomColumns.forEach(column => {
            customHeader.push({
                cells: [{
                    data: {
                        type: 'String',
                        value: column.name,
                    },
                    styleId: 'font_size10'
                }]
            });
        });
        const paramsForExport = {
            fileName: fileName,
            sheetName: sheetName,
            skipHeader: skipHeader,
            processCellCallback: (param) => {
                if (typeof param.value === 'string' && !param.value) {
                    return '';
                }
                if (param.node.group) {
                    if (typeof param.value === 'string' && param.value) {
                        // param.value = '-> 1460995200000'
                        const values = param.value.trim().split('->');
                        values.splice(0, 1);
                        let value = '';
                        if (values.length === 0) {
                            value = `-> ${BLANK_VALUE} `;
                        } else {
                            values.forEach((item, i) => {
                                const tempValue = workflowService.cellRenderer(groupColumnSettings[i], {
                                    value: item.trim()
                                });
                                value = `${value} -> ${tempValue || BLANK_VALUE} `;
                            });
                        }
                        return value;
                    }
                }

                return GridLayoutUtility.parseCellValue(param.column.colDef, param, utils);
            }
        };
        gridApi.exportDataAsExcel(paramsForExport);
    }

    destroy() {
        this._destroySubscriptions.forEach(item => item.unsubscribe());
    }

    /**
     * extracts current grid column settings from the given grid.
     * @param gridApi the grid api
     * @param colApi the grid column api
     * @returns the grid layout setting model for persistence.
     */
    getGridLayoutFromGrid(gridApi: GridApi, colApi: ColumnApi): GridLayoutModel {
        const layoutModel = new GridLayoutModel();
        if (gridApi && colApi) {
            layoutModel.Columns = colApi.getColumnState();
            layoutModel.SortModel = layoutModel.Columns
                .filter(function (s) {
                    return s.sort != null;
                })
                .map(function (s) {
                    return { colId: s.colId, sort: s.sort };
                });

            layoutModel.Filters = gridApi.getFilterModel();
            layoutModel.GroupColumns = colApi.getRowGroupColumns().map(col => col.getColId()).join(GROUP_DELIMINITER);
        }

        return layoutModel;
    }

    /**
     * obtains the saved setting for current screen.
     * @returns an array of saved screen settings, the first one is the setting for current screen.
     */
    getWorkFlowScreenFromStore(): Observable<Array<SavedScreen>> {
        return this.store.pipe(
            select(screensSelector),
            filter(screens => !!screens),
            map(screens => {
                return screens.filter((item) => item.type === businessConstants.screenType.workflow && item.id === this.screenId);
            }), take(1));
    }

    /**
     * obtains the grid layout settings for current screen through store query.
     * @returns the grid layout setting model
     */
    getGridLayoutFromStore(): Observable<GridLayoutModel> {
        return this.getWorkFlowScreenFromStore().map(screens => screens && screens.length > 0 && screens[0].configuration ? screens[0].configuration.gridLayout : null);
    }

    /**
     * merges the saved grid layout with predefined column definitions
     * @param gridLayout the saved grid layout settings.
     * @param coldDefs the predefined column definitions.
     * @returns the merged result with saved layout settings.
     */
    getMergedColDefs(gridLayout: GridLayoutModel, coldDefs: Array<ColDef | any>): Array<ColDef | any> {
        const sortedColumns = [];
        if (gridLayout) {
            const layoutColIds = [];
            // user saved columns
            gridLayout.Columns.forEach(col => {
                const matchedColDef = coldDefs.find(colDef => colDef.colId === col.colId);
                const matchedSortSetting = gridLayout.SortModel && gridLayout.SortModel.find(sc => sc.colId === col.colId);
                if (matchedColDef) {
                    // column exists in both layout settings and current column defintions.
                    matchedColDef.hide = col.hide;
                    matchedColDef.pinned = col.pinned;
                    if (matchedSortSetting) {
                        matchedColDef.sort = matchedSortSetting.sort;
                    }
                    matchedColDef.rowGroupIndex = col.rowGroupIndex;
                    sortedColumns.push(matchedColDef);
                }

                layoutColIds.push(col.colId);
            });
            // system added columns
            DefaultColumnIDs.forEach(defaultColumnID => {
                if (!layoutColIds.includes(defaultColumnID)) {
                    const matchedColDef = coldDefs.find(colDef => colDef.colId === defaultColumnID);
                    if (matchedColDef) {
                        sortedColumns.push(matchedColDef);
                    }
                    layoutColIds.push(defaultColumnID);
                }
            });
            // new added adhocs. find out those only exist in current column definitions, append them in the list and show them.
            coldDefs.filter(colDef => !layoutColIds.includes(colDef.colId)).forEach(hiddenCol => {
                hiddenCol.hide = false;
                sortedColumns.push(hiddenCol);
            });
        }

        return sortedColumns;
    }

    /**
     * saves the given grid layout settings.
     * @param gridLayout the grid layout setting model
     * @returns an observable indicating the saving operation is processed successfully or not.
     */
    saveGridLayout(gridLayout: GridLayoutModel): Observable<boolean> {
        return new Observable(
            observer => {
                this._destroySubscriptions.push(
                    this.getWorkFlowScreenFromStore().subscribe(screens => {
                        if (screens && screens.length > 0 && screens[0].configuration) {
                            screens[0].configuration.gridLayout = gridLayout;
                            this.screenService.update(screens[0]).subscribe(r => {
                                this.store.dispatch({
                                    type: SystemActionTypes.EDIT_SCREEN,
                                    payload: {
                                        type: businessConstants.screenType.workflow,
                                        ...screens[0]
                                    },
                                });
                                observer.next(true);
                            }, err => observer.error(err));
                        }
                    }));
                // returns a teardown function.
                return () => { };
            }
        );
    }
}
function setSortModel(arg0: import("ag-grid-community").ColumnState[]) {
    throw new Error('Function not implemented.');
}

